/**
 * Canned replies admin — saved snippets agents insert with "/" in the inbox
 * composer. `{customer_name}` is replaced with the customer's first name.
 */
import { useState } from 'react';
import { Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useCannedReplies,
  useCreateCannedReply,
  useDeleteCannedReply,
  usePatchCannedReply,
  type CrmCannedReply,
} from '@/api/bi/crmConversations';
import { CHANNEL_META } from '@/components/crm/inbox/channelMeta';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

interface EditorState {
  id: number | null;
  title: string;
  shortcut: string;
  body: string;
  channels: string[];
  is_active: boolean;
}

const EMPTY: EditorState = { id: null, title: '', shortcut: '', body: '', channels: [], is_active: true };

export function CrmCannedRepliesPage() {
  const list = useCannedReplies(true);
  const create = useCreateCannedReply();
  const patch = usePatchCannedReply();
  const remove = useDeleteCannedReply();
  const [editor, setEditor] = useState<EditorState | null>(null);

  const rows = list.data?.results ?? [];

  async function save() {
    if (!editor) return;
    const body = {
      title: editor.title,
      shortcut: editor.shortcut,
      body: editor.body,
      channels: editor.channels,
      is_active: editor.is_active,
    };
    try {
      if (editor.id == null) await create.mutateAsync(body);
      else await patch.mutateAsync({ id: editor.id, body });
      notify.success('Canned reply saved');
      setEditor(null);
    } catch {
      notify.error('Could not save canned reply');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="Canned replies"
        description="Saved snippets agents insert by typing / in the inbox composer. Use {customer_name} to personalise. Leave channels empty to show on all channels."
        actions={
          <Button className="gap-1" onClick={() => setEditor({ ...EMPTY })}>
            <Plus className="h-4 w-4" /> New reply
          </Button>
        }
      />

      {editor ? (
        <Card className="mb-4 border-primary/40">
          <CardBody className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Title</Label>
              <Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} />
            </div>
            <div>
              <Label>Shortcut (typed after /)</Label>
              <Input
                value={editor.shortcut}
                placeholder="e.g. refund"
                onChange={(e) => setEditor({ ...editor, shortcut: e.target.value.replace(/\s/g, '') })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Reply text</Label>
              <textarea
                className="min-h-[96px] w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                value={editor.body}
                onChange={(e) => setEditor({ ...editor, body: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Channels (none selected = all channels)</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {Object.entries(CHANNEL_META).map(([key, meta]) => {
                  const active = editor.channels.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() =>
                        setEditor({
                          ...editor,
                          channels: active
                            ? editor.channels.filter((c) => c !== key)
                            : [...editor.channels, key],
                        })
                      }
                      className={cn(
                        'rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset',
                        active
                          ? 'bg-primary/10 text-primary ring-primary/40'
                          : 'text-muted-foreground ring-border hover:bg-muted',
                      )}
                    >
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editor.is_active}
                onChange={(e) => setEditor({ ...editor, is_active: e.target.checked })}
              />
              Active (visible to agents)
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditor(null)}>
                Cancel
              </Button>
              <Button onClick={() => void save()} disabled={!editor.title.trim() || !editor.body.trim()}>
                Save
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {list.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <ErrorState
          description="Could not load canned replies."
          onRetry={() => void list.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Zap className="h-5 w-5" />}
          title="No canned replies yet"
          description="Create snippets for the answers your team types most."
        />
      ) : (
        <Card>
          <CardBody className="divide-y divide-border p-0">
            {rows.map((r) => (
              <CannedRow
                key={r.id}
                reply={r}
                onEdit={() =>
                  setEditor({
                    id: r.id,
                    title: r.title,
                    shortcut: r.shortcut,
                    body: r.body,
                    channels: r.channels,
                    is_active: r.is_active,
                  })
                }
                onDelete={async () => {
                  await remove.mutateAsync(r.id);
                  notify.success('Canned reply deleted');
                }}
              />
            ))}
          </CardBody>
        </Card>
      )}
    </>
  );
}

function CannedRow({
  reply: r,
  onEdit,
  onDelete,
}: {
  reply: CrmCannedReply;
  onEdit: () => void;
  onDelete: () => Promise<void>;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{r.title}</span>
          {r.shortcut ? <Badge tone="primary">/{r.shortcut}</Badge> : null}
          {!r.is_active ? <Badge tone="neutral">inactive</Badge> : null}
          {r.channels.length > 0
            ? r.channels.map((c) => (
                <Badge key={c} tone="neutral">
                  {CHANNEL_META[c]?.label ?? c}
                </Badge>
              ))
            : <Badge tone="neutral">All channels</Badge>}
        </div>
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{r.body}</p>
      </div>
      <div className="flex shrink-0 gap-1">
        <button type="button" title="Edit" onClick={onEdit} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted">
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Delete"
          onClick={() => void onDelete()}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
